/**
 * Utility para calcular fechas de entrega basadas en reglas de negocio complejas.
 */

/**
 * Suma horas hábiles a una fecha considerando:
 * - Horario laboral del cliente
 * - Días hábiles del cliente
 * - Horas de prioridad
 *
 * @param {Date} startDate - Fecha de inicio (ej: ahora mismo)
 * @param {number} hoursToAdd - Horas hábiles a sumar
 * @param {object} businessHours - Configuración de horarios del cliente
 * @returns {Date} Fecha/hora calculada de entrega
 */
function addBusinessHoursExact(startDate, hoursToAdd, businessHours) {
  let currentDate = new Date(startDate);

  const {from: workStartHour, to: workEndHour} = businessHours.hours;
  const businessDays = businessHours.days.map(d => parseInt(d, 10));

  const isBusinessDay = (date) => businessDays.includes(date.getDay());

  const moveToValidTime = (date) => {
    while (!isBusinessDay(date)) {
      date.setDate(date.getDate() + 1);
      date.setHours(workStartHour, 0, 0, 0);
    }
    if (date.getHours() < workStartHour) {
      date.setHours(workStartHour, 0, 0, 0);
    }
    if (date.getHours() >= workEndHour) {
      do {
        date.setDate(date.getDate() + 1);
      } while (!isBusinessDay(date));
      date.setHours(workStartHour, 0, 0, 0);
    }
    return date;
  };

  currentDate = moveToValidTime(currentDate);

  while (hoursToAdd > 0) {
    const endOfWork = new Date(currentDate);
    endOfWork.setHours(workEndHour, 0, 0, 0);

    const diffMs = endOfWork - currentDate;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (hoursToAdd <= diffHours) {
      currentDate = new Date(currentDate.getTime() + hoursToAdd * 60 * 60 * 1000);
      return currentDate;
    } else {
      hoursToAdd -= diffHours;
      currentDate = endOfWork;
      do {
        currentDate.setDate(currentDate.getDate() + 1);
      } while (!isBusinessDay(currentDate));
      currentDate.setHours(workStartHour, 0, 0, 0);
    }
  }
  return currentDate;
}

function normalizeStartDate(date, businessHours) {
  const {from: workStartHour, to: workEndHour} = businessHours.hours;
  const businessDays = businessHours.days.map(d => parseInt(d, 10));
  const isBusinessDay = (d) => businessDays.includes(d.getDay());

  let newDate = new Date(date);

  // Si no es día hábil → mueve al siguiente
  while (!isBusinessDay(newDate)) {
    newDate.setDate(newDate.getDate() + 1);
    newDate.setHours(workStartHour, 0, 0, 0);
  }

  // Solo si ya pasó la hora de cierre, no si está justo en el cierre
  if (newDate.getHours() > workEndHour ||
    (newDate.getHours() === workEndHour && newDate.getMinutes() > 0)) {
    do {
      newDate.setDate(newDate.getDate() + 1);
    } while (!isBusinessDay(newDate));
    newDate.setHours(workStartHour, 0, 0, 0);
  }

  // Si es antes de la hora de inicio → mover al inicio
  if (newDate.getHours() < workStartHour) {
    newDate.setHours(workStartHour, 0, 0, 0);
  }

  return newDate;
}

/**
 * Calcula la fecha de entrega según la prioridad del cliente
 * Soporta:
 * - Clientes con deliveryRules (casuística personalizada)
 * - Clientes con priorityHours (lógica antigua)
 */
export function calculateDeliveryDate(priority, clientConfig) {
  const baseDate = normalizeStartDate(new Date(), clientConfig.businessHours);

  // Reglas personalizadas
  if (clientConfig.deliveryRules && clientConfig.deliveryRules.length > 0) {
    const rule = clientConfig.deliveryRules.find(r => r.priority === priority);
    if (rule) {
      switch (rule.type) {
        case "WORKING_HOURS":
          return addBusinessHoursExact(baseDate, rule.hours, clientConfig.businessHours);

        case "CALENDAR_HOURS":
          return new Date(baseDate.getTime() + rule.hours * 3600 * 1000);

        case "CONDITIONAL":
          return applyConditionalRules(baseDate, rule.conditions);

        default:
          console.warn(`Tipo de regla no reconocido: ${rule.type}`);
      }
    }
  }

  // Fallback con priorityHours
  if (clientConfig.businessHours && clientConfig.businessHours.priorityHours) {
    const hoursToAdd = clientConfig.businessHours.priorityHours[priority];
    if (!hoursToAdd) return baseDate;

    if (hoursToAdd < 24) {
      return addBusinessHoursExact(baseDate, hoursToAdd, clientConfig.businessHours);
    }
    return new Date(baseDate.getTime() + hoursToAdd * 3600 * 1000);
  }

  return baseDate;
}

function applyConditionalRules(startDate, conditions) {
  const currentHour = startDate.getHours();
  const currentMinute = startDate.getMinutes();
  const currentTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;

  for (const cond of conditions) {
    if (currentTime >= cond.from && currentTime <= cond.to) {
      const result = new Date(startDate);
      const [h, m] = cond.deadline.split(':');
      result.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);

      if (cond.dayMode === "NEXT_DAY") {
        result.setDate(result.getDate() + 1);
      }
      return result;
    }
  }
  return startDate; // fallback
}

/**
 * Prioridad NORMAL en pedidos de materiales:
 * - NO considera horario laboral
 * - SOLO considera días de atención
 * - Horas corridas (ej: 24h)
 */
export function calculateNormalDeliveryDate(startDate, businessHours) {

  // Si el cliente no tiene configuración de horarios
  if (!businessHours || !businessHours.days || businessHours.days.length === 0) {
    return new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
  }

  const hoursToAdd = businessHours.priorityHours && businessHours.priorityHours.normal
    ? businessHours.priorityHours.normal
    : 24;

  const businessDays = businessHours.days.map(function (d) {
    return parseInt(d, 10);
  });

  const isBusinessDay = function (date) {
    return businessDays.indexOf(date.getDay()) !== -1;
  };

  let currentDate = new Date(startDate);
  let remainingHours = hoursToAdd;

  while (remainingHours > 0) {
    currentDate.setHours(currentDate.getHours() + 1);

    if (isBusinessDay(currentDate)) {
      remainingHours--;
    }
  }

  return currentDate;
}



// WEBPACK FOOTER //
// ./src/util/deliveryDateCalculator.js